import React, { useEffect, useRef, useState } from 'react';
import VideoInfoWrapper from '../components/Video/VideoInfoWrapper';
import VideoReplayWrapper from '../components/Video/VideoReplayWrapper';
import VideoPlayer from '../components/Video/VideoPlayer';
import { useNavigate, useParams } from 'react-router-dom';
import ModalWrapper from '../components/Modal/ModalWrapper';
import DownloadItemsWrapper from '../components/Common/DownloadItemsWrapper';
import { useTranslation } from 'react-i18next';
import useGetLocationData from '../hooks/useGetLocationData';
import useGetBeatInfo from '../hooks/useGetBeatInfo';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useDialog from '../hooks/useDialog';
import { languageState } from '../recoil/language/languageAtom';
import useApi from '../hooks/useApi';
import { loadingSpinnerState } from '../recoil/loadingSpinner/loadingSpinnerAtom';
import VideoDefaultScreen from '../components/Video/VideoDefaultScreen';
import ERROR_CODES from '../constants/errorCodes';
import SCREEN_SIZES from '../constants/screenSizes';
import { playerIsPlayingState } from '../recoil/player/playerIsPlayingAtom';
import { countryState } from '../recoil/country/countryAtom';
import * as Sentry from '@sentry/react';

const VideoClipDetail = () => {
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isMobileScreen, setIsMobileScreen] = useState(window.innerWidth <= SCREEN_SIZES.MOBILE_SCREEN_WIDTH);
  const [clipInfo, setClipInfo] = useState(null);
  const playerRef = useRef(null);
  const [keyPairId, setKeyPairId] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [signature, setSignature] = useState(null);
  const { id } = useParams();
  const { t } = useTranslation();
  const accessToken = localStorage.getItem('access_token');
  const countryData = useRecoilValue(countryState);
  const navigate = useNavigate();
  const { showDialog, hideDialog } = useDialog();
  const apiV1 = useApi('v1');
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const selectedLanguage = useRecoilValue(languageState);
  const setIsLoading = useSetRecoilState(loadingSpinnerState);
  const setPlayerIsPlayingState = useSetRecoilState(playerIsPlayingState);

  // 단순 로그인 확인용
  const { isLogin, hasAccessToken } = useGetBeatInfo(accessToken, countryData?.country);

  // 미구매 상품인 경우 targetId;
  let targetId;

  let locationData = {
    location: 'seoul', // local 환경에서 기본값
  };

  // 배포 환경에서만 실행(local 환경에서 cors error)
  if (process.env.NODE_ENV === 'production') {
    locationData = useGetLocationData('https://location.mubeat.tv/');
  }

  // signed url 관련 미들웨어 정의(signed cookie로 바꿀 경우 불필요함)
  // function secureHLSMiddleware() {
  //   return function () {
  //     const srcHandler = {
  //       setSource(srcObj, next) {
  //         console.log('srcObj', srcObj);
  //         // src URL에 쿼리 매개변수 추가
  //         const modifiedUrl = `${srcObj.src}?Policy=${policy}&Signature=${signature}&Key-Pair-Id=${keyPairId}`;
  //         srcObj.src = modifiedUrl;
  //         next(null, srcObj); // 업데이트된 소스로 진행
  //       },
  //     };
  //     return srcHandler;
  //   };
  // }

  // videojs.use('application/x-mpegURL', secureHLSMiddleware());

  const videoSources = clipInfo?.clipList?.map((clip) => ({
    ...clip,
    src: `${clip.url}?Policy=${policy}&Signature=${signature}&Key-Pair-Id=${keyPairId}`,
    // src: clip.url, // // signed cookie일 때 사용
    type: 'application/x-mpegURL',
    withCredentials: false,
  }));

  const videoOptions = {
    preload: true,
    controls: true,
    responsive: true,
    fluid: true,
    liveui: true,
    html5: {
      vhs: {
        overrideNative: true,
      },
      // nativeAudioTracks: false,
      // nativeVideoTracks: false,
    },
    controlBar: {
      pictureInPictureToggle: false,
      remainingTimeDisplay: false,
      volumePanel: {
        inline: true,
      },
      currentTimeDisplay: true,
      durationDisplay: true,
      qualitySelector: true,
    },
    // 단순 테스트용 url
    // sources: [
    //   {
    //     src: 'https://bitdash-a.akamaihd.net/content/sintel/hls/playlist.m3u8',
    //     type: 'application/x-mpegURL',
    //   },
    // ],
    sources: videoSources,
  };

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('exp');
    Sentry.setUser(null);
  };

  // 클립이 없는 경우
  const alertNoClip = () => {
    showDialog({
      dialogType: 'AlertDialog',
      dialogProps: {
        title: t('video_clip_not_found_msg'),
        confirmText: t('confirm'),
        handleConfirm: () => {
          hideDialog();
        },
      },
    });
  };
  // 미구매 상품의 경우 targetId로 보내는 alertDialog
  const alertNoPurchase = () => {
    showDialog({
      dialogType: 'AlertDialog',
      dialogProps: {
        title: t('not_purchased_state_msg'),
        confirmText: t('confirm'),
        handleConfirm: () => {
          navigate(`/product-video-detail/${targetId}`);
        },
      },
    });
  };

  const handlePlayerReady = (player) => {
    playerRef.current = player;

    // player.on('waiting', () => {
    //   // videojs.log('waiting');
    // });

    player.on('playing', () => {
      setPlayerIsPlayingState(true);
    });

    player.on('pause', () => {
      setPlayerIsPlayingState(false);
    });

    player.on('ended', () => {
      setPlayerIsPlayingState(false);
    });

    player.on('dispose', () => {
      setPlayerIsPlayingState(false);
    });
  };

  useEffect(() => {
    return () => {
      // 플레이어 dispose로 메모리 누수 방지
      if (playerRef.current) {
        playerRef.current.dispose();
      }
    };
  }, []);

  // params.id값 바뀌면 리랜더링
  useEffect(() => {}, [id]);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileScreen(window.innerWidth <= SCREEN_SIZES.MOBILE_SCREEN_WIDTH);
    };

    window.addEventListener('resize', checkScreenSize);

    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);

  //   // 모바일에선 재생 불가 처리(다운로드 모달 출력)
  useEffect(() => {
    if (isMobileScreen) {
      setIsDownloadModalOpen(true);
    } else {
      setIsDownloadModalOpen(false);
    }
  }, [isMobileScreen]);

  useEffect(() => {
    const getVideoInfo = async () => {
      setIsLoading(true);
      setIsLocalLoading(true);

      const lang = selectedLanguage === 'ko' ? 'KO' : 'EN';

      if (countryData && countryData.country) {
        try {
          const response = await apiV1.get(
            `clip/play/${id}?country=${countryData?.country}&location=${locationData?.location}&lang=${lang}`,
            {
              headers: {
                Authorization: accessToken,
              },
            },
          );

          setClipInfo(response?.data);

          const {
            ['CloudFront-Key-Pair-Id']: keyPairId,
            ['CloudFront-Policy']: policy,
            ['CloudFront-Signature']: signature,
          } = response.data.cookieList;

          setKeyPairId(keyPairId);
          setPolicy(policy);
          setSignature(signature);

          // 쿠키 세팅
          // document.cookie = `CloudFront-Key-Pair-Id=${keyPairId}`;
          // document.cookie = `CloudFront-Policy=${policy}`;
          // document.cookie = `CloudFront-Signature=${signature}`;
        } catch (error) {
          if (error.code === 'ERR_NETWORK' || error.code === 'ERR_BAD_RESPONSE') {
            navigate('/error');
          }

          const errorCode = error?.response && error?.response.data && error?.response.data.code;
          if (errorCode === ERROR_CODES.exclusiveAccessForPurchasers) {
            targetId = error?.response?.data?.data?.targetIds[0];
            alertNoPurchase();
          }

          if (errorCode === ERROR_CODES.notFoundVideo) {
            alertNoClip();
          } else {
            console.log(errorCode);
          }
        } finally {
          setIsLoading(false);
          setIsLocalLoading(false);
        }
      }
    };
    if (hasAccessToken === false || isLogin === false) {
      clearSession();
      navigate('/');
    }

    if (isLogin === true) getVideoInfo();
  }, [countryData, id, isLogin, hasAccessToken]);

  return (
    <>
      {isLogin ? (
        <>
          <div className={'ml-page pt-80'}>
            <div className='page-inner'>
              <div className='layout-inner'>
                <div className='ml-video-detail-page-wrapper'>
                  {!isMobileScreen ? (
                    <div className='ml-video-layout-wrapper'>
                      <div className='ml-video-left-wrapper'>
                        {isLocalLoading ? (
                          <VideoDefaultScreen />
                        ) : (
                          <VideoPlayer
                            options={videoOptions}
                            onReady={handlePlayerReady}
                            videoType={'replay'}
                            policy={policy}
                            keyPairId={keyPairId}
                            signature={signature}
                            clipInfo={clipInfo}
                            id={id}
                            countryData={countryData}
                          />
                        )}

                        <VideoInfoWrapper videoType={'replay'} clipInfo={clipInfo} id={id} />
                      </div>
                      <div className='ml-video-right-wrapper'>
                        <VideoReplayWrapper clipInfo={clipInfo} countryData={countryData} />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <ModalWrapper
            isOpen={isDownloadModalOpen}
            size={'sm'}
            type={'download'}
            noScroll={true}
            onClose={() => {
              navigate(-1);
            }}
          >
            <DownloadItemsWrapper title={t('mubeat_download_msg')} />
          </ModalWrapper>
        </>
      ) : null}
    </>
  );
};

export default VideoClipDetail;
